import { forwardRef, memo, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useRenderColumns } from '../../columns';
import { SnapCarousel } from '../../main';
import { SnapCarouselWrapper, tableCarouselItemMixin } from '../components';
import { useLayout } from '../layout-context';

export const LayoutComparisonTableMobile = memo(
  forwardRef<HTMLDivElement, unknown>((_, ref) => {
    const {
      breakpoints: { isMediumBreakpoint, isSmallBreakpoint },
      rowsCount,
      setLayout
    } = useLayout();
    const columns = useRenderColumns();

    useLayoutEffect(() => {
      if (!isMediumBreakpoint) {
        setLayout('comparisonTable');
      }
    }, [isMediumBreakpoint, setLayout]);

    return (
      <SnapCarouselWrapper ref={ref} component="layout-comparison-table-mobile">
        <Container>
          <StyledSnapCarousel
            $columnsCount={columns.length}
            $rowsCount={rowsCount}
            $isSmallBreakpoint={isSmallBreakpoint}
          >
            {columns}
          </StyledSnapCarousel>
        </Container>
      </SnapCarouselWrapper>
    );
  })
);

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'layout-comparison-table-mobile',
    element: 'container'
  })
})`
  display: flex;
  overflow: hidden;
  border-radius: ${({
    theme: {
      column: { cornerRadius }
    }
  }) => cornerRadius}px;

  & > div {
    position: static;
  }

  &::after {
    content: '';
    position: absolute;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: ${({
      theme: {
        column: { cornerRadius }
      }
    }) => cornerRadius}px;
  }
`;

const StyledSnapCarousel = styled(SnapCarousel).attrs<{
  $columnsCount: number;
  $rowsCount: number;
  $isSmallBreakpoint: boolean;
}>({
  columnsGap: 0,
  className: createReadableClass({
    component: 'layout-comparison-table-mobile',
    element: 'snap-carousel'
  })
})`
  display: grid;
  margin: 0 auto;
  grid-template-columns: repeat(
    ${({ $columnsCount }) => $columnsCount},
    ${({ $columnsCount, $isSmallBreakpoint }) => {
      if ($columnsCount === 1) {
        return '100%';
      }

      if ($isSmallBreakpoint) {
        return '85%';
      }

      if ($columnsCount === 2) {
        return '50%';
      }

      return '42.5%';
    }}
  );
  ${tableCarouselItemMixin}
`;
